import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/admin/AuthContext";
import { BrandMark } from "@/components/politics/brand";

export default function AdminLogin() {
  const { user, loginWithGoogle, loading } = useAuth();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const handleLogin = async () => {
    setRedirecting(true);
    setLoginError(null);
    try {
      await loginWithGoogle();
    } catch {
      setRedirecting(false);
      setLoginError("Não foi possível iniciar o login com Google. Tente novamente.");
    }
  };

  useEffect(() => {
    if (!loading && user) {
      window.location.replace("/admin");
    }
  }, [loading, user]);

  return (
    <main
      data-testid="admin-login-page"
      className="relative isolate overflow-hidden min-h-screen grid place-items-center bg-[#F5FAFB] px-6"
    >
      <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-70" />
      <div className="absolute -top-32 right-[-10%] -z-10 h-[480px] w-[480px] rounded-full blur-3xl opacity-30"
           style={{ background: "radial-gradient(closest-side, #208BFF, transparent 70%)" }} />

      <div className="w-full max-w-md rsm-rise">
        <Link
          to="/politics"
          data-testid="admin-login-back-home"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#51565F] hover:text-[#208BFF] transition-colors"
        >
          <ArrowLeft size={14} /> Voltar para o site
        </Link>

        <div className="mt-6 rounded-2xl border border-[#1B1D1D]/10 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2.5">
            <BrandMark size={36} color="#1B1D1D" />
            <span className="font-display text-base font-semibold text-[#1B1D1D]">
              RSM <span className="text-[#7D8188]">Admin</span>
            </span>
          </div>

          <h1 className="mt-8 font-display text-3xl font-bold tracking-tighter text-[#1B1D1D] leading-tight">
            Acesso ao painel
          </h1>
          <p className="mt-3 text-sm text-[#51565F]">
            Entre com a conta Google associada ao seu perfil de parceiro ou à
            equipe RSM.
          </p>

          <button
            onClick={handleLogin}
            disabled={redirecting}
            data-testid="admin-login-google"
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border border-[#1B1D1D]/15 bg-white px-6 py-3.5 text-sm font-semibold text-[#1B1D1D] hover:border-[#208BFF] hover:text-[#208BFF] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-[transform,border-color,color,opacity] duration-300 ease-out"
          >
            {redirecting ? <Loader2 size={18} className="animate-spin" /> : <GoogleLogo />}
            {redirecting ? "Redirecionando ao Google…" : "Entrar com Google"}
          </button>
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-xs text-[#D94A0A]">
              {loginError}
            </p>
          )}

          <p className="mt-6 text-xs text-[#7D8188] leading-relaxed">
            O acesso ao perfil é liberado para o e-mail cadastrado como contato
            do parceiro. Se o seu e-mail não estiver vinculado, fale com a RSM.
          </p>
        </div>

        {location.state?.from && (
          <p className="mt-4 text-center text-xs text-[#7D8188]">
            Após o login você será levado para {location.state.from}.
          </p>
        )}
      </div>
    </main>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.51 8.18c0-.57-.05-1.13-.15-1.68H9v3.17h4.21a3.6 3.6 0 0 1-1.56 2.36v1.96h2.52c1.48-1.36 2.34-3.36 2.34-5.81Z"
        fill="#4285F4"
      />
      <path
        d="M9 17c2.1 0 3.86-.69 5.16-1.87l-2.52-1.96c-.7.47-1.6.74-2.64.74-2.03 0-3.74-1.37-4.36-3.21H1.95v2.02A8 8 0 0 0 9 17Z"
        fill="#34A853"
      />
      <path
        d="M4.64 10.7A4.81 4.81 0 0 1 4.39 9c0-.59.1-1.16.25-1.7V5.28H1.95A8 8 0 0 0 1 9c0 1.29.31 2.51.86 3.6l2.78-1.9Z"
        fill="#FBBC05"
      />
      <path
        d="M9 4.08c1.15 0 2.18.4 2.99 1.17l2.24-2.24A7.69 7.69 0 0 0 9 1 8 8 0 0 0 1.95 5.28L4.73 7.3C5.36 5.45 7.07 4.08 9 4.08Z"
        fill="#EA4335"
      />
    </svg>
  );
}
